/**
 * English games: Reading, Vocabulary, Spelling, Grammar, Punctuation,
 * Creative Writing, Cloze, Language Techniques.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/** Reading & Comprehension */
const generateReadingQuestion = (difficulty) => {
    const items = [
        { passage: "The old lighthouse stood tall against the stormy sky. Sailors had depended on its beam for over a hundred years.", q: "What had sailors depended on?", answer: "The beam of the lighthouse", options: ["The beam of the lighthouse", "The stormy sky", "The tall tower", "The sailors' eyes"] },
        { passage: "Maya opened the dusty book. Inside, she found a map leading to a hidden garden.", q: "What did Maya find inside the book?", answer: "A map", options: ["A map", "A garden", "Dust", "A key"] },
        { passage: "The dog whimpered by the door. Its tail wagged when it saw its owner.", q: "How did the dog feel when it saw its owner?", answer: "Happy", options: ["Happy", "Sad", "Afraid", "Angry"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, passage: item.passage, answer: item.answer, options: shuffle([...item.options]), type: "eng-reading", mode: "reading", visualType: "passage" };
};

/** Vocabulary - synonyms, antonyms, word meanings */
const generateVocabularyQuestion = (difficulty) => {
    const items = [
        { q: "Which word means the same as 'happy'?", answer: "Joyful", options: ["Joyful", "Sad", "Angry", "Tired"], word: "happy", symbol: "≈" },
        { q: "Which word means the opposite of 'brave'?", answer: "Cowardly", options: ["Cowardly", "Bold", "Strong", "Clever"], word: "brave", symbol: "≠" },
        { q: "In 'The storm was fierce', what does 'fierce' mean?", answer: "Violent or powerful", options: ["Violent or powerful", "Gentle", "Quiet", "Cold"], word: "fierce", symbol: "?" },
        { q: "Choose the best word: The dog was very ___ when it saw food.", answer: "Excited", options: ["Excited", "Sleepy", "Blue", "Heavy"], word: "___", symbol: "→" },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, answer: item.answer, options: shuffle([...item.options]), type: "eng-vocabulary", mode: "vocabulary", visualType: "word-match", word: item.word, symbol: item.symbol };
};

/** Spelling - homophones, tricky spellings */
const generateSpellingQuestion = (difficulty) => {
    const items = [
        { q: "Which is the correct spelling? (belonging to them)", answer: "Their", options: ["Their", "Thier", "Ther", "Thare"] },
        { q: "Choose the correct spelling: (place)", answer: "There", options: ["There", "Their", "They're", "Thare"] },
        { q: "Which word is spelt correctly? (i before e except after c)", answer: "Receive", options: ["Receive", "Recieve", "Receve", "Recive"] },
        { q: "Which spelling is correct for 'believe'?", answer: "Believe", options: ["Believe", "Belive", "Beleive", "Believ"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, answer: item.answer, options: shuffle([...item.options]), type: "eng-spelling", mode: "spelling", visualType: "spelling-choice" };
};

/** Grammar - parts of speech, tenses */
const generateGrammarQuestion = (difficulty) => {
    const items = [
        { q: "Which word is a verb?", sentence: "The cat sat on the mat.", answer: "Sat", options: ["Sat", "Cat", "On", "Mat"] },
        { q: "Which word is an adjective?", sentence: "The big red ball bounced.", answer: "Big", options: ["Big", "Ball", "Bounced", "The"] },
        { q: "Choose the correct verb:", sentence: "She ___ to school yesterday.", answer: "Went", options: ["Went", "Go", "Going", "Goes"] },
        { q: "Which sentence is grammatically correct?", sentence: null, answer: "The children are playing in the park.", options: ["The children are playing in the park.", "The children is playing in the park.", "The children am playing in the park.", "The children be playing in the park."] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, sentence: item.sentence, answer: item.answer, options: shuffle([...item.options]), type: "eng-grammar", mode: "grammar", visualType: "sentence" };
};

/** Punctuation */
const generatePunctuationQuestion = (difficulty) => {
    const items = [
        { q: "Which sentence is punctuated correctly?", answer: "Where are you going?", options: ["Where are you going?", "Where are you going", "where are you going?", "Where are you going!"] },
        { q: "Choose the correct apostrophe: The ___ toys are new.", answer: "children's", options: ["children's", "childrens", "childrens'", "childrens's"] },
        { q: "Which sentence has correct commas?", answer: "I bought apples, bananas, and oranges.", options: ["I bought apples, bananas, and oranges.", "I bought apples bananas and oranges.", "I bought apples, bananas and oranges", "i bought apples, bananas, and oranges."] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, answer: item.answer, options: shuffle([...item.options]), type: "eng-punctuation", mode: "punctuation", visualType: "punctuation" };
};

/** Creative Writing - best sentence/paragraph choices */
const generateCreativeWritingQuestion = (difficulty) => {
    const items = [
        { q: "Which is the best opening sentence for a story about a storm?", answer: "Dark clouds gathered on the horizon as the wind began to howl.", options: ["Dark clouds gathered on the horizon as the wind began to howl.", "The storm was bad.", "I like storms.", "It rained."] },
        { q: "Which sentence shows the most imagination?", answer: "The moon smiled down like a silver coin in the velvet sky.", options: ["The moon smiled down like a silver coin in the velvet sky.", "The moon was in the sky.", "It was nighttime.", "The moon shone."] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, answer: item.answer, options: shuffle([...item.options]), type: "eng-creative-writing", mode: "creative-writing", visualType: "creative-choice" };
};

/** Cloze & Sentence-Completion */
const generateClozeQuestion = (difficulty) => {
    const items = [
        { q: "Fill in the blank:", sentence: "The cat ___ on the sofa.", answer: "Sat", options: ["Sat", "Sit", "Sitting", "Sits"] },
        { q: "Complete the sentence:", sentence: "She ___ her homework before dinner.", answer: "Finished", options: ["Finished", "Finish", "Finishing", "Finishes"] },
        { q: "Choose the best word:", sentence: "It was so ___ that we needed a torch.", answer: "Dark", options: ["Dark", "Bright", "Happy", "Loud"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, sentence: item.sentence, answer: item.answer, options: shuffle([...item.options]), type: "eng-cloze", mode: "cloze", visualType: "cloze-blank" };
};

/** Language Techniques - similes, metaphors, etc. */
const generateLanguageTechniquesQuestion = (difficulty) => {
    const items = [
        { q: "Which phrase is a simile?", example: "as...as / like", answer: "As brave as a lion", options: ["As brave as a lion", "The lion was brave", "Lions are brave", "A brave lion"] },
        { q: "Which word is an example of onomatopoeia?", example: "sound words", answer: "Bang", options: ["Bang", "Quiet", "Loud", "Sound"] },
        { q: "'Her smile was sunshine' is an example of:", example: "comparison", answer: "Metaphor", options: ["Metaphor", "Simile", "Fact", "Question"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    return { question: item.q, example: item.example, answer: item.answer, options: shuffle([...item.options]), type: "eng-language-techniques", mode: "language-techniques", visualType: "technique" };
};

export const generateEnglishQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "eng-reading": return generateReadingQuestion(difficulty);
        case "eng-vocabulary": return generateVocabularyQuestion(difficulty);
        case "eng-spelling": return generateSpellingQuestion(difficulty);
        case "eng-grammar": return generateGrammarQuestion(difficulty);
        case "eng-punctuation": return generatePunctuationQuestion(difficulty);
        case "eng-creative-writing": return generateCreativeWritingQuestion(difficulty);
        case "eng-cloze": return generateClozeQuestion(difficulty);
        case "eng-language-techniques": return generateLanguageTechniquesQuestion(difficulty);
        default: return generateVocabularyQuestion(difficulty);
    }
};
